'use client';

import {
  CategoryScale,
  Chart as ChartJS,
  Colors,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  type Plugin,
  PointElement,
  type ScriptableLineSegmentContext,
  TimeScale,
  Tooltip,
  type TooltipItem,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { differenceInDays, subDays } from 'date-fns';
import { he } from 'date-fns/locale/he';
import Slider from 'rc-slider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'rc-slider/assets/index.css';
import './chart.css';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import '@/lib/time-adapter';

ChartJS.register(
  Colors,
  CategoryScale,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Filler,
  Tooltip,
  annotationPlugin,
);

const verticalLinePlugin: Plugin = {
  id: 'verticalLinePlugin',
  // biome-ignore lint/suspicious/noExplicitAny: chart.js internal tooltip._active is not in public types
  beforeDatasetsDraw: (chart: any) => {
    if (chart.tooltip?._active?.length) {
      const ctx = chart.ctx;
      const activePoint = chart.tooltip._active[0];
      const x = activePoint.element.x;
      const yAxis = chart.scales.y;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, yAxis.top);
      ctx.lineTo(x, yAxis.bottom);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(0, 119, 204, 1)';
      ctx.stroke();
      ctx.restore();
    }
  },
};

ChartJS.register(verticalLinePlugin);

interface ChartPoint {
  x: Date;
  y: number | null;
  data: { date: string; price: number | null; listPrice?: number };
}

interface PriceData {
  date: string;
  price: number | null;
  listPrice?: number;
}

interface StatsData {
  max: { date: Date | string; price: number };
  min: { date: Date | string; price: number };
  meanPrice: number;
  current: { date: Date | string; price: number };
}

interface ChartProps {
  stats: StatsData;
  prices: PriceData[];
  showRangeSelection?: boolean;
  maintainAspectRatio?: boolean;
}

interface RangeOption {
  label: string;
  days: number | undefined;
}

const MS_IN_DAY = 1000 * 60 * 60 * 24;

export default function Chart({
  stats,
  prices,
  showRangeSelection = true,
  maintainAspectRatio = true,
}: ChartProps) {
  const { max: highestPrice, min: lowestPrice, meanPrice, current } = stats;
  // biome-ignore lint/suspicious/noExplicitAny: react-chartjs-2 ref type conflicts with ChartJS generic
  const chartRef = useRef<any>(null);
  const [isClient, setIsClient] = useState(false);

  const totalDays = Math.max(differenceInDays(new Date(), new Date(prices[0].date)), 1);
  const [range, setRange] = useState<[number, number]>([0, totalDays]);
  const [selectedValue, setSelectedValue] = useState<string>('אחר');

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  const getDateFromMS = useCallback(
    (ms: number): Date => new Date(Date.now() - MS_IN_DAY * ms),
    [],
  );

  const generateDataArray = useCallback(
    (range: [number, number]): ChartPoint[] => {
      const [rangeEnd, rangeStart] = range;
      const points = prices.map(
        (r: PriceData): ChartPoint => ({
          x: new Date(r.date),
          y: r.price,
          data: r,
        }),
      );

      const currentPrice = prices.at(-1)?.price ?? null;
      const firstPointData = prices[0];
      const firstPrice = firstPointData.price;
      const firstPointDate = getDateFromMS(rangeStart);
      const lastPointDate = getDateFromMS(rangeEnd);

      const midPoints = points.filter(
        (p: ChartPoint) => p.x >= firstPointDate && p.x <= lastPointDate,
      );

      const firstPointPrice = points
        .slice()
        .reverse()
        .find((p: ChartPoint) => p.x < firstPointDate)?.y;
      const lastPointData = midPoints.at(-1);
      const lastPointPrice = lastPointData?.y;

      const lastPoint: ChartPoint = {
        x: lastPointDate,
        y: lastPointPrice === undefined ? currentPrice : lastPointPrice,
        data: lastPointData?.data ?? prices[prices.length - 1],
      };

      const firstPoint: ChartPoint = {
        x: firstPointDate,
        y: firstPointPrice === undefined ? firstPrice : firstPointPrice,
        data: firstPointData,
      };

      const arr = [firstPoint, ...midPoints, lastPoint];

      for (let i = 1; i < arr.length; i++) {
        const { x, y } = arr[i];
        if (y == null) {
          const p1: ChartPoint = { x, y: arr[i - 1].y, data: arr[i - 1].data };
          arr.splice(i, 0, p1);
          i++;
        }
      }

      arr[arr.length - 1].data = arr[arr.length - 2].data;
      arr[0].data = arr[1].data;

      return arr;
    },
    [prices, getDateFromMS],
  );

  const dataArray = useMemo(() => generateDataArray(range), [range, generateDataArray]);

  const padding = (highestPrice.price - lowestPrice.price) * 0.1;

  const priceAnnotation = (text: string, value: number, color?: string, position?: string) => ({
    type: 'line' as const,
    borderColor: color ?? 'black',
    borderWidth: 6,
    borderDash: [6, 6],
    borderDashOffset: 0,
    label: {
      drawTime: 'afterDatasetsDraw' as const,
      display: false,
      borderRadius: 4,
      borderWidth: 0,
      content: `${text}: ${value}`,
      rotation: 'auto' as const,
      position: (position ?? 'center') as 'start' | 'center' | 'end',
    },
    scaleID: 'y',
    value,
  });

  const annotations: Record<string, ReturnType<typeof priceAnnotation>> = {};
  if (highestPrice.price !== lowestPrice.price) {
    annotations.a0 = priceAnnotation('המחיר הנוכחי', current.price);
    if (lowestPrice.price === current.price) {
      annotations.a0.borderColor = 'rgba(0, 166, 45, 0.5)';
      annotations.a0.label.content = `המחיר הנמוך ביותר / ${annotations.a0.label.content}`;
    } else {
      annotations.a1 = priceAnnotation(
        'המחיר הנמוך ביותר',
        lowestPrice.price,
        'rgba(0, 166, 45, 0.5)',
      );
    }
    if (highestPrice.price === current.price) {
      annotations.a0.borderColor = 'rgba(255, 78, 78, 0.5)';
      annotations.a0.label.content = `המחיר הגבוה ביותר / ${annotations.a0.label.content}`;
    } else {
      annotations.a2 = priceAnnotation(
        'המחיר הגבוה ביותר',
        highestPrice.price,
        'rgba(255, 78, 78, 0.5)',
      );
    }
    if (meanPrice === current.price) {
      annotations.a0.label.content = `המחיר הממוצע / ${annotations.a0.label.content}`;
    } else {
      annotations.a3 = priceAnnotation('המחיר הממוצע', meanPrice, 'rgba(149, 0, 255, 0.5)');
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: ChartOptions<'line'> is too strict for annotation plugin and time scale stepSize
  const options: any = {
    responsive: true,
    animation: {
      duration: 100,
      easing: 'linear',
    },
    plugins: {
      title: { display: false },
      legend: { display: false },
      annotation: {
        // biome-ignore lint/suspicious/noExplicitAny: chartjs-plugin-annotation EventContext type is complex
        enter({ element }: { element: any }) {
          element.label.options.display = true;
          return true;
        },
        // biome-ignore lint/suspicious/noExplicitAny: chartjs-plugin-annotation EventContext type is complex
        leave({ element }: { element: any }) {
          element.label.options.display = false;
          return true;
        },
        common: {
          drawTime: 'beforeDatasetsDraw',
        },
        annotations,
      },
      tooltip: {
        displayColors: false,
        mode: 'nearest' as const,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyAlign: 'center' as const,
        bodySpacing: 4,
        bodyFont: {
          size: 18,
          weight: 'bold',
        },
        footerFont: {
          size: 10,
        },
        callbacks: {
          label: (item: TooltipItem<'line'>) => `${item.formattedValue}₪`,
          beforeFooter: ([{ raw }]: TooltipItem<'line'>[]) =>
            (raw as ChartPoint)?.data?.listPrice &&
            (raw as ChartPoint).data.listPrice !== (raw as ChartPoint).data.price
              ? 'פורסם כמבצע'
              : '',
          footer: ([{ raw }]: TooltipItem<'line'>[]) =>
            (raw as ChartPoint)?.data?.listPrice &&
            (raw as ChartPoint).data.listPrice !== (raw as ChartPoint).data.price
              ? `מחיר ללא מבצע: ${(raw as ChartPoint).data.listPrice}₪`
              : '',
        },
        footerAlign: 'center' as const,
        intersect: false,
      },
    },
    interaction: {
      mode: 'index' as const,
      axis: 'x',
      intersect: false,
    },
    maintainAspectRatio,
    scales: {
      x: {
        adapters: {
          date: {
            locale: he,
          },
        },
        type: 'time' as const,
        time: {
          tooltipFormat: 'Pp',
          unit: 'week' as const,
          stepSize: 2,
        },
      },
      y: {
        display: true,
        suggestedMin: lowestPrice.price - padding,
        suggestedMax: highestPrice.price + padding,
        title: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        left: 5,
        right: 5,
        top: 5,
        bottom: 5,
      },
    },
  };

  const chartData = {
    datasets: [
      {
        borderWidth: 2,
        radius: 2,
        hitRadius: 0.1,
        segment: {
          borderDash: (ctx: ScriptableLineSegmentContext) =>
            ctx.p0.skip || ctx.p1.skip ? [1, 3] : undefined,
          backgroundColor: (ctx: ScriptableLineSegmentContext) =>
            ctx.p0.skip || ctx.p1.skip ? 'rgba(0, 0, 0, 0)' : undefined,
        },
        spanGaps: true,
        stepped: 'before' as const,
        fill: true,
        data: dataArray,
      },
    ],
  };

  const rangeOptions: RangeOption[] = [
    ...[
      { label: 'שבוע אחרון', days: 7 },
      { label: 'חודש אחרון', days: 30 },
      { label: 'שלושה חודשים אחרונים', days: 90 },
      { label: 'חצי שנה אחרונה', days: 182 },
      { label: 'שנה אחרונה', days: 356 },
      { label: 'הכל', days: totalDays },
    ].filter((x) => x.days !== undefined && x.days <= totalDays),
    { label: 'אחר', days: undefined },
  ];

  const handleRangeChange = (value: string) => {
    setSelectedValue(value);
    const option = rangeOptions.find((x) => x.label === value);
    if (option === undefined || option.days === undefined) {
      return;
    }
    setRange([0, option.days]);
  };

  const handleSliderChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      setRange([value[0], value[1]]);
      setSelectedValue('אחר');
    }
  };

  const generateMarks = useCallback(() => {
    const marks: Record<number, string> = {};
    const pipStep = Math.max(1, Math.floor(totalDays / 10));
    const threshold = pipStep * 0.5;

    for (let i = 0; i <= totalDays; i += pipStep) {
      const date = subDays(new Date(), i);
      marks[i] = date.toLocaleDateString('he-IL', {
        month: 'short',
        day: 'numeric',
        year: '2-digit',
      });
    }

    const existingKeys = Object.keys(marks).map(Number);

    const hasNearZero = existingKeys.some((key) => Math.abs(key - 0) < threshold);
    if (!hasNearZero) {
      marks[0] = new Date().toLocaleDateString('he-IL', {
        month: 'short',
        day: 'numeric',
        year: '2-digit',
      });
    }

    const hasNearEnd = existingKeys.some((key) => Math.abs(key - totalDays) < threshold);
    if (!hasNearEnd) {
      const startDate = subDays(new Date(), totalDays);
      marks[totalDays] = startDate.toLocaleDateString('he-IL', {
        month: 'short',
        day: 'numeric',
        year: '2-digit',
      });
    }

    return marks;
  }, [totalDays]);

  const marks = useMemo(() => generateMarks(), [generateMarks]);

  const chartKey = useMemo(() => {
    if (prices.length === 0) return 'empty';
    return `chart-${prices[0].date}-${prices[prices.length - 1].date}-${prices.length}`;
  }, [prices]);

  if (!isClient) {
    return (
      <div className="flex h-[400px] w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
        <div className="text-gray-400">טוען גרף...</div>
      </div>
    );
  }

  return (
    <>
      <Line key={chartKey} ref={chartRef} data={chartData} options={options} />

      {totalDays > 2 && showRangeSelection && (
        <div className="mb-4 ps-2 pe-5">
          <div className="flex w-full items-center justify-center">
            <div className="pe-2 text-gray-600 dark:text-gray-400">טווח זמן:</div>
            <Select value={selectedValue} onValueChange={handleRangeChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="בחר טווח זמן" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>בחר טווח זמן</SelectLabel>
                  <SelectSeparator />
                  {rangeOptions.map((option) => (
                    <SelectItem
                      key={option.label}
                      value={option.label}
                      disabled={option.days === undefined}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-8 pb-6">
            <Slider
              range
              min={0}
              max={totalDays}
              value={[range[0], range[1]]}
              onChange={handleSliderChange}
              marks={marks}
              step={1}
              reverse
              pushable
              allowCross={false}
              styles={{
                track: {
                  backgroundColor: 'rgb(59 130 246)',
                },
                tracks: {
                  backgroundColor: 'rgb(59 130 246)',
                },
                handle: {
                  borderColor: 'rgb(59 130 246)',
                  backgroundColor: 'white',
                  opacity: 1,
                },
                rail: {
                  backgroundColor: 'rgb(226 232 240)',
                },
              }}
              className="px-2"
            />
          </div>
        </div>
      )}
    </>
  );
}
